import { useEffect, useState } from "react";
import { askAI, type AnalyzeResponse, type AskResponse, type RegulationChunk } from "../services/api";
import Pipeline from "./Pipeline";
import Markdown from "./Markdown";
import { useLang } from "../hooks/useLang";
import { useAnswerLang } from "../hooks/useAnswerLang";

type Props = {
  res: AnalyzeResponse;
  question: string;
  /** Kullanici sayfadan ayrilip donduyse true — cevap ekranda kalir ama
   * "onceki sorgu" olarak isaretlenir. Yeni istek GITMEZ; etiket olmadan
   * kullanici bunu soru tekrar sorulmus saniyordu. */
  stale?: boolean;
  /** Cevabin geldigi an (epoch ms) — etikette gosterilir. */
  answeredAt?: number | null;
};

function sourceTag(c: RegulationChunk, fallback: string): string {
  const i = c.id.indexOf("_");
  return i > 0 ? c.id.slice(0, i) : fallback;
}

/** Kaynak satırının başlığı. İki kusur düzeltiliyor (aynısı sitede de vardı,
 *  2026-08-17):
 *  1. "IEC 60601-1 — chunk 685" → chunk sırası İÇ OFSET, mevzuat konumu değil.
 *     Denetimde savunulacak bir atıf değil; standardın adı tek başına dürüst.
 *     Kaynağı ingest/standards_loader.go (madde tespit edilemeyen parça). Orası
 *     düzeltildi ama Meili'deki mevcut parçalar eski başlığı taşıyor, o yüzden
 *     burada da kırpılıyor.
 *  2. Madde iki kez yazılıyordu: rozet olarak c.article, başlıkta da "§ <madde>". */
function citationTitle(c: RegulationChunk): string {
  return String(c.heading || "").replace(/\s+—\s+chunk\s+\d+\s*$/i, "");
}

/** Madde rozeti, başlıkta zaten geçiyorsa tekrar edilmez. */
function showArticleBadge(c: RegulationChunk): boolean {
  const art = String(c.article || "");
  if (!art) return false;
  return !citationTitle(c).includes("§ " + art);
}

/** Cevapta geçen [#n] numaraları — TEKİL.
 *
 *  Liste aramanın getirdiği TÜM parçaları gösteriyor; cevap yalnızca bir kısmını
 *  atıflıyor. Hangilerinin gerçekten kullanıldığı görünmüyordu ve başlık
 *  "yalnızca bu dokümanlar kullanıldı" diyordu — denetim kanıtı üreten bir
 *  yüzeyde savunulamaz bir iddia. */
function citedNumbers(text: string): Set<number> {
  const out = new Set<number>();
  const re = /\[#(\d+)\]/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(String(text || ""))) !== null) out.add(parseInt(m[1], 10));
  return out;
}

function regulationBadge(reg: string): string {
  return reg === "MDR" ? "badge badge-mdr" : "badge badge-ivdr";
}

/** Cevap metnindeki [#n] referanslarini tiklanabilir pill'lere donusturur. */
function renderAnswer(text: string, onCite: (n: number) => void, titleTmpl: string) {
  const parts: (string | JSX.Element)[] = [];
  const re = /\[#(\d+)\]/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let key = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const n = parseInt(m[1], 10);
    parts.push(
      <button
        key={`c${key++}`}
        className="cite-pill"
        onClick={() => onCite(n)}
        title={titleTmpl.replace("{n}", String(n))}
      >
        #{n}
      </button>
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

export default function AnswerView({ res, question, stale = false, answeredAt = null }: Props) {
  const { t, lang } = useLang();
  const { lang: answerLang } = useAnswerLang();
  const [expandedCite, setExpandedCite] = useState<Set<number>>(new Set());
  const [focused, setFocused] = useState<number | null>(null);

  // Accordion durumlari: RAG bolumu acik baslar, chat kapali.
  const [openRag, setOpenRag] = useState(true);
  const [openChat, setOpenChat] = useState(false);

  // "AI Answer" bolumu durumu (sağlayıcı adı kullanıcıya gösterilmez)
  const [chat, setChat] = useState<AskResponse | null>(null);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatErr, setChatErr] = useState<string | null>(null);

  // Strict RAG dokümanlarda cevap bulamadıysa yanıt bu cümleyle başlar.
  const insufficient = res.answer.trimStart().startsWith("I do not have sufficient information");
  // Hangi kaynaklar gerçekten atıflandı. Otomatik AI yanıtına düşüldüğünde
  // chat metni gösteriliyor; sayım GÖSTERİLEN metne bakar, kaynak listesi de
  // o metnin atıflarına göre işaretlenir.
  const cited = citedNumbers(chat?.answer ?? res.answer);

  // Yeni sonuç gelince chat durumunu sıfırla. Strict RAG dokümanlarda cevap
  // bulamadıysa (yetersiz-bilgi mesajı) genel AI yanıtını OTOMATİK iste ve
  // sonucu RAG gövdesinde yetersiz mesajın yerine göster (madde 1, 2026-07-22).
  useEffect(() => {
    setChat(null);
    setChatErr(null);
    setChatLoading(false);
    setOpenChat(false);
    if (insufficient) {
      void ask({ auto: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [res]);

  function toggle(i: number) {
    setExpandedCite((prev) => {
      const next = new Set(prev);
      next.has(i) ? next.delete(i) : next.add(i);
      return next;
    });
  }

  function focusCitation(n: number) {
    const idx = n - 1;
    setOpenRag(true);
    setFocused(idx);
    setExpandedCite((prev) => new Set(prev).add(idx));
    setTimeout(() => {
      const el = document.getElementById(`cite-${idx}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 0);
    setTimeout(() => setFocused(null), 1600);
  }

  async function ask(opts?: { auto?: boolean }) {
    setChatLoading(true);
    setChatErr(null);
    try {
      const r = await askAI(question, answerLang);
      setChat(r);
      // Otomatik fallback'te akordeonu açma; yanıt RAG gövdesinde gösteriliyor.
      if (!opts?.auto) setOpenChat(true);
    } catch (e: unknown) {
      setChatErr(e instanceof Error ? e.message : String(e));
    } finally {
      setChatLoading(false);
    }
  }

  const chatTitle = t("chat_section_title");

  return (
    <>
      {/* Bayat cevap uyarisi — renk TEK BASINA tasimaz, metinle sunulur
          (siyah-beyaz baskida ve renk korlugunde de okunur). */}
      {stale && (
        <div className="answer-stale" role="status">
          <strong>{t("answer_stale_note")}</strong>
          {answeredAt ? (
            <span className="answer-stale-when">
              {new Date(answeredAt).toLocaleString(lang === "en" ? "en-GB" : "tr-TR")}
            </span>
          ) : null}
          <span className="answer-stale-hint">{t("answer_stale_hint")}</span>
        </div>
      )}

      {/* ===== Bolum 1: AI-Powered Answer (strict RAG) ===== */}
      <section className="acc">
        <button className="acc-head acc-head-rag" onClick={() => setOpenRag((v) => !v)}>
          <span className="acc-title-group">
            <span className="acc-title">{t("rag_section_title")}</span>
            <small className="acc-sub">
              {t("pipe_ai_banner_sub")}{" "}
              {res.chunks_used > 0 && t("pipe_ai_banner_sources_used").replace("{n}", String(res.chunks_used))}
            </small>
          </span>
          <span className="acc-chev">{openRag ? "▲" : "▼"}</span>
        </button>

        {openRag && (
          <div className="acc-body">
            <Pipeline steps={res.pipeline} />
            <div className="answer-question">{t("answer_question_label")}: {question}</div>
            <div className="answer-body">
              {!insufficient ? (
                renderAnswer(res.answer, focusCitation, t("answer_citation_n_title"))
              ) : chat && !chatLoading ? (
                <>
                  <div className="rag-fallback-note">
                    {t("rag_fallback_note")}
                  </div>
                  <div className="md-body"><Markdown text={chat.answer} /></div>
                </>
              ) : chatErr ? (
                <>
                  {renderAnswer(res.answer, focusCitation, t("answer_citation_n_title"))}
                  <div className="error">{t("chat_error")}: {chatErr}</div>
                </>
              ) : (
                <div className="loading"><div className="spinner" /><div>{t("chat_asking")}</div></div>
              )}
            </div>

            {(res.citations?.length ?? 0) > 0 && (
              <div className="citations">
                <h4>{t("answer_sources_title")}</h4>
                {/* Tarandı / atıflandı ayrımı: sayı tek başına "cevap 8 kaynağa
                    dayanıyor" diye okunuyordu. */}
                <p className="citations-count">
                  {t("answer_sources_count")
                    .replace("{n}", String(res.citations?.length ?? 0))
                    .replace("{c}", String(cited.size))}
                </p>
                <div className="citation-grid">
                  {(res.citations ?? []).map((c, i) => {
                    const expanded = expandedCite.has(i);
                    const isFocused = focused === i;
                    const isCited = cited.has(i + 1);
                    return (
                      <div
                        key={c.id}
                        id={`cite-${i}`}
                        className={`citation ${isFocused ? "focused" : ""} ${isCited ? "is-cited" : "not-cited"}`}
                      >
                        <div className="citation-top">
                          <span className="badge badge-num">#{i + 1}</span>
                          {/* Renk tek başına durum taşımaz (design-system §2):
                              rozet metniyle birlikte, baskıda da okunur. */}
                          <span className={"badge " + (isCited ? "badge-cited" : "badge-uncited")}>
                            {isCited ? t("answer_cited_mark") : t("answer_not_cited")}
                          </span>
                          <span className={regulationBadge(c.regulation)}>{c.regulation}</span>
                          <span className="badge badge-src">{sourceTag(c, t("answer_citation_tag_default"))}</span>
                          {showArticleBadge(c) && <span className="badge badge-src">{c.article}</span>}
                        </div>
                        <div className="citation-title">{citationTitle(c)}</div>
                        <div className={`citation-body ${expanded ? "expanded" : ""}`}>
                          {c.content}
                        </div>
                        {c.content.length > 280 && (
                          <button className="citation-toggle" onClick={() => toggle(i)}>
                            {expanded ? t("answer_citation_collapse") : t("answer_citation_expand")}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </section>

      {/* ===== Bolum 2: AI Answer (konu-kilitli sohbet; sağlayıcı adı gizli) ===== */}
      {/* Strict RAG yetersizse cevap zaten Bölüm 1'de otomatik AI'dan geliyor;
          bu bölümü ("Ask AI" butonu dahil) gizle — tekrar sormaya gerek yok (madde 1). */}
      {!insufficient && (
      <section className="acc">
        <button className="acc-head" onClick={() => setOpenChat((v) => !v)}>
          <span className="acc-title">{chatTitle}</span>
          <span className="acc-chev">{openChat ? "▲" : "▼"}</span>
        </button>

        {openChat && (
          <div className="acc-body">
            {!chat && !chatLoading && !chatErr && (
              <p className="chat-intro">{t("chat_intro")}</p>
            )}

            {chatLoading && (
              <div className="loading">
                <div className="spinner" />
                <div>{t("chat_asking")}</div>
              </div>
            )}

            {chatErr && <div className="error">{t("chat_error")}: {chatErr}</div>}

            {chat && !chatLoading && (
              <>
                <div className="answer-question">{t("answer_question_label")}: {question}</div>
                <div className="answer-body md-body"><Markdown text={chat.answer} /></div>
                <p className="chat-hint">{t("chat_hint")}</p>
              </>
            )}

            <button className="chat-ask-btn" onClick={() => ask()} disabled={chatLoading}>
              {chatLoading ? t("chat_asking") : chat ? t("chat_reask_btn") : t("chat_ask_btn")}
            </button>
          </div>
        )}
      </section>
      )}
    </>
  );
}
